/**
 * React Query hooks for Schema Management
 *
 * Provides hooks for applying and managing structured data (schema.org)
 * across blog posts and service pages with automatic polling for run status.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { useTenantStore } from "@/stores/useTenantStore";
import { schemaService } from "../services/schemaService";
import type {
	ArticleSchemaStatus,
	ContentFilters,
	ContentListingResponse,
	FAQContentType,
	FAQSchemaStatus,
	RunStatusResponse,
	SchemaOverview,
	SchemaRunResponse,
} from "../types/schema";

// Query keys for cache management
export const schemaKeys = {
	all: ["schema"] as const,
	overview: (tenantId: string) =>
		[...schemaKeys.all, "overview", tenantId] as const,
	runStatus: (runId: string) => [...schemaKeys.all, "run", runId] as const,
	contentListing: (
		tenantId: string,
		filters?: ContentFilters,
		page?: number,
		pageSize?: number,
	) =>
		[...schemaKeys.all, "content", tenantId, filters, page, pageSize] as const,
};

/**
 * Hook to get schema implementation overview
 */
export function useSchemaOverview() {
	const { currentTenant } = useTenantStore();

	return useQuery<SchemaOverview>({
		queryKey: schemaKeys.overview(currentTenant ?? ""),
		queryFn: () => schemaService.getOverview(currentTenant!),
		enabled: !!currentTenant,
		staleTime: 60_000, // 60s — schema overview changes infrequently, reduces initial page load burst
		gcTime: 5 * 60 * 1000, // 5 minutes
	});
}

/**
 * Hook to apply Article schema to all published posts
 */
export function useApplyArticleSchema() {
	const { currentTenant } = useTenantStore();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (): Promise<SchemaRunResponse> =>
			schemaService.applyArticleSchema(currentTenant!),
		onSuccess: (data) => {
			toast.success(`Article schema application started: ${data.message}`);
			queryClient.invalidateQueries({ queryKey: schemaKeys.all });
		},
		onError: (error: Error) => {
			toast.error(`Failed to apply Article schema: ${error.message}`);
		},
	});
}

/**
 * Hook to apply FAQ schema to posts or service pages
 */
export function useApplyFAQSchema() {
	const { currentTenant } = useTenantStore();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (contentType: FAQContentType): Promise<SchemaRunResponse> =>
			schemaService.applyFAQSchema(currentTenant!, contentType),
		onSuccess: (data) => {
			toast.success(`FAQ schema application started: ${data.message}`);
			queryClient.invalidateQueries({ queryKey: schemaKeys.all });
		},
		onError: (error: Error) => {
			toast.error(`Failed to apply FAQ schema: ${error.message}`);
		},
	});
}

/**
 * Hook to run full schema implementation (Article + FAQ for both posts and service pages)
 */
export function useRunFullImplementation() {
	const { currentTenant } = useTenantStore();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (): Promise<SchemaRunResponse[]> =>
			schemaService.runFullImplementation(currentTenant!),
		onSuccess: (data) => {
			const runCount = data.length;
			toast.success(
				`Full schema implementation started: ${runCount} run(s) initiated`,
			);
			queryClient.invalidateQueries({ queryKey: schemaKeys.all });
		},
		onError: (error: Error) => {
			toast.error(`Failed to run full implementation: ${error.message}`);
		},
	});
}

/**
 * Hook to fetch content with schema status for the selection UI
 *
 * @param filters - Content type and schema status filters
 * @param page - Current page number (1-indexed)
 * @param pageSize - Number of items per page
 * @param enabled - Whether to enable the query (default: true)
 */
export function useContentListing(
	filters?: ContentFilters,
	page?: number,
	pageSize?: number,
	enabled = true,
) {
	const { currentTenant } = useTenantStore();

	return useQuery<ContentListingResponse>({
		queryKey: schemaKeys.contentListing(
			currentTenant ?? "",
			filters,
			page,
			pageSize,
		),
		queryFn: () =>
			schemaService.getContentWithSchemaStatus(
				currentTenant!,
				filters,
				page,
				pageSize,
			),
		enabled: enabled && !!currentTenant,
	});
}

/**
 * Hook to apply Article schema to selected items
 */
export function useApplyArticleSchemaSelective() {
	const { currentTenant } = useTenantStore();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (itemIds: number[]): Promise<SchemaRunResponse> =>
			schemaService.applyArticleSchemaSelective(currentTenant!, {
				item_ids: itemIds,
			}),
		onSuccess: (data) => {
			toast.success(`Article schema application started: ${data.message}`);
			queryClient.invalidateQueries({ queryKey: schemaKeys.all });
		},
		onError: (error: Error) => {
			toast.error(`Failed to apply Article schema: ${error.message}`);
		},
	});
}

/**
 * Hook to apply FAQ schema to selected items
 */
export function useApplyFAQSchemaSelective() {
	const { currentTenant } = useTenantStore();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			contentType,
			itemIds,
		}: {
			contentType: FAQContentType;
			itemIds: number[];
		}): Promise<SchemaRunResponse> =>
			schemaService.applyFAQSchemaSelective(currentTenant!, contentType, {
				item_ids: itemIds,
			}),
		onSuccess: (data) => {
			toast.success(`FAQ schema application started: ${data.message}`);
			queryClient.invalidateQueries({ queryKey: schemaKeys.all });
		},
		onError: (error: Error) => {
			toast.error(`Failed to apply FAQ schema: ${error.message}`);
		},
	});
}

/**
 * Hook to poll run status with automatic refresh while running
 *
 * @param runId - The run ID to track (can be null to disable)
 * @param enabled - Whether to enable the query (default: true)
 */
export function useSchemaRunStatus(runId: string | null, enabled = true) {
	return useQuery<RunStatusResponse>({
		queryKey: schemaKeys.runStatus(runId ?? ""),
		queryFn: () => {
			if (!runId) {
				throw new Error("Run ID is required");
			}
			return schemaService.getRunStatus(runId);
		},
		enabled: enabled && !!runId,
		// Poll every 10 seconds while running, stop when complete or failed
		// NOTE: Increased from 2s to 10s to reduce HTTP/1.1 connection pressure
		// (3 users × 2s polling was creating request storms that saturated servers)
		refetchInterval: (data) => {
			if (!data) return false;
			return data.status === "running" ? 10000 : false;
		},
		// Keep previous data while refetching to prevent UI flickering
		placeholderData: (previousData) => previousData,
	});
}

/**
 * Hook to manage multiple active schema runs with polling
 *
 * Tracks multiple run IDs and provides methods to add/remove them.
 * Use this when running full implementation or managing multiple concurrent runs.
 */
export function useActiveSchemaRuns() {
	const [activeRunIds, setActiveRunIds] = useState<string[]>([]);

	const addRun = (runId: string) => {
		setActiveRunIds((prev) => {
			if (prev.includes(runId)) return prev;
			return [...prev, runId];
		});
	};

	const addRuns = (runIds: string[]) => {
		setActiveRunIds((prev) => {
			const newIds = runIds.filter((id) => !prev.includes(id));
			return [...prev, ...newIds];
		});
	};

	const removeRun = (runId: string) => {
		setActiveRunIds((prev) => prev.filter((id) => id !== runId));
	};

	const clearAll = () => {
		setActiveRunIds([]);
	};

	return { activeRunIds, addRun, addRuns, removeRun, clearAll };
}

/**
 * Composite hook that combines run initiation with automatic status tracking
 *
 * Example usage:
 * ```tsx
 * const { applyArticleSchema, activeRuns } = useSchemaWithTracking();
 *
 * const handleApply = async () => {
 *   await applyArticleSchema();
 * };
 * ```
 */
export function useSchemaWithTracking() {
	const { activeRunIds, addRun, addRuns, removeRun } = useActiveSchemaRuns();

	const applyArticleMutation = useApplyArticleSchema();
	const applyFAQMutation = useApplyFAQSchema();
	const fullImplementationMutation = useRunFullImplementation();

	const applyArticleSchema = async () => {
		const result = await applyArticleMutation.mutateAsync();
		addRun(result.run_id);
		return result;
	};

	const applyFAQSchema = async (contentType: FAQContentType) => {
		const result = await applyFAQMutation.mutateAsync(contentType);
		addRun(result.run_id);
		return result;
	};

	const runFullImplementation = async () => {
		const results = await fullImplementationMutation.mutateAsync();
		addRuns(results.map((r) => r.run_id));
		return results;
	};

	return {
		applyArticleSchema,
		applyFAQSchema,
		runFullImplementation,
		activeRunIds,
		removeRun,
		isLoading:
			applyArticleMutation.isPending ||
			applyFAQMutation.isPending ||
			fullImplementationMutation.isPending,
	};
}

// Re-export types for convenience
export type {
	ContentFilters,
	ContentListingResponse,
	FAQContentType,
	RunStatusResponse,
	SchemaOverview,
	SchemaRunResponse,
};

// Type aliases for component usage
export type SchemaStats = ArticleSchemaStatus | FAQSchemaStatus;
export type SchemaRunStatus = RunStatusResponse;
